import "./Login.scss";

import {
  AppBar,
  Box,
  Card,
  Container,
  Grid,
  isWidthDown,
  makeStyles,
  Tab,
  Tabs,
  Typography,
  withWidth,
} from "@material-ui/core";
import { ApiErrors } from "common/backend/api-errors/errors";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { SubmitButton } from "common/components/input/elements";
import { UserField, UserInputTypeKey } from "common/components/input/fields";
import {
  GetCurrentUserLazy,
  RegisterUser,
} from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { AccountType, RegisterUserManageable } from "common/store/user/types";
import { errorMessages } from "common/utils/validation";
import { ContentWrapper } from "components/ContentWrapper";
import { LanguageSwitch } from "components/LanguageSwitch";
import { Form, Formik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { DocumentType, ShowFile, showFile } from "services/fake/fakeDocumentManager";

const appBarStyle = makeStyles({
  colorPrimary: {
    borderTopRightRadius: 25,
    borderTopLeftRadius: 25,
    boxShadow: "unset",
    position: "relative",
    top: "25px",
  },
});

const indicatorStyle = makeStyles({
  root: {
    borderTopRightRadius: 25,
    borderTopLeftRadius: 25,
  },
  indicator: {
    backgroundColor: "#0059E2",
    height: 4,
  },
});

const tabStyle = makeStyles({ textColorInherit: { borderRadius: "0px !important" } });

function Register({ width }: any) {
  // State
  const [remarkVisible, setRemarkVisible] = useState(false);
  const [error, setError] = useState("");

  // Hooks
  const history = useHistory();
  const { t } = useTranslation();

  const { execute: executeGetCurrentUser } = useCustomQuery(GetCurrentUserLazy({}));

  const { execute: registerUser } = useCustomQuery(RegisterUser({
    onSuccess: () => {
      executeGetCurrentUser();
    },
    onError: (error: ApiErrors) => {
      if (error === ApiErrors.USER_DUPLICATE_EMAIL) {
        setError(t("Duplicate email"));
      }
    },
  }));

  // Options
  const initialValues = new RegisterUserManageable();

  const appBar = appBarStyle();
  const indicator = indicatorStyle();
  const tab = tabStyle();

  // Validation
  const onValidate = (values: RegisterUserManageable) => {
    return new RegisterUserManageable(values).validate(
      Object.keys(values),
      errorMessages
    );
  };

  const onSubmit = async (values: RegisterUserManageable) => {
    await registerUser({ variables: { input: new RegisterUserManageable(values).serialize() } });
  };

  const onLinkClick = (
    e:any, fileToShow: any, link: any
  ) => {
    e.preventDefault();

    if (fileToShow) {
      showFile(
        ShowFile.Open,
        fileToShow
      )
    } else if (link) {
      window.open(
        link,
        "_blank"
      )
    }
  };

  return (
    <ContentWrapper>
      <Box className="login">
        {remarkVisible &&
        <Box
          bgcolor="transparent"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Card className={"card"}>
            <Box className="language-remark">
              <Typography variant="subtitle2" color="textSecondary">
                {t("Please remark, that - although you are viewing our site in English language - the FinPro platform is operating exclusively under German jurisdiction and any activity on our website is exclusively subject to German law and regulation.")}
              </Typography>
            </Box>
          </Card>
        </Box>
        }

        <Formik
          initialValues={initialValues}
          validate={onValidate}
          onSubmit={onSubmit}
        >
          {(props) => {
            const {
              setFieldValue,
              values,
            } = props;

            return (
              <>
                <AppBar position="static" classes={appBar}>
                  <Tabs
                    centered
                    variant="fullWidth"
                    classes={indicator}
                    value={values.accountType}
                    onChange={(
                      _: any, value: number
                    ) =>
                      setFieldValue(
                        "accountType",
                        value
                      )
                    }
                    aria-label="simple tabs example"
                  >
                    <Tab
                      label={t("Private")}
                      classes={tab}
                      id="simple-tab-0"
                      aria-controls="simple-tabpanel-0"
                    />

                    <Tab
                      label={t("Company")}
                      classes={tab}
                      id="simple-tab-1"
                      aria-controls="simple-tabpanel-1"
                    />
                  </Tabs>
                </AppBar>

                <Form>
                  <Container
                    className={
                      isWidthDown(
                        "xs",
                        width
                      )
                        ? "container--mobile"
                        : "container"
                    }
                  >
                    <LanguageSwitch
                      onLanguageChanged={() => setRemarkVisible(true)}
                    />

                    <Grid container spacing={6}>
                      <Grid item xs={12}>
                        <Typography className="title" color="primary">
                          {t("Register")}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <UserField id={"firstName" as UserInputTypeKey} {...props} />
                      </Grid>

                      <Grid item xs={12}>
                        <UserField id={"lastName" as UserInputTypeKey} {...props} />
                      </Grid>

                      <Grid item xs={12}>
                        <UserField id={"email" as UserInputTypeKey} {...props} />
                      </Grid>

                      <Grid item xs={12}>
                        <UserField id={"password" as UserInputTypeKey} {...props} />
                      </Grid>

                      <Grid item xs={12}>
                        <UserField id={"confirmPassword" as UserInputTypeKey} {...props} />
                      </Grid>

                      {values.accountType === AccountType.Company &&
                        <>
                          <Grid item xs={12}>
                            <UserField
                              id={"company" as UserInputTypeKey}
                              subkey="registrationNumber"
                              {...props}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <UserField
                              id={"company" as UserInputTypeKey}
                              subkey="name"
                              {...props}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <UserField
                              id={"company" as UserInputTypeKey}
                              subkey="legalEntityType"
                              {...props}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <UserField
                              id={"company" as UserInputTypeKey}
                              subkey="tradeRegister"
                              {...props}
                            />
                          </Grid>
                        </>
                      }

                      <Grid item xs={12}>
                        <UserField id={"subscribedToProductUpdates" as UserInputTypeKey} {...props} />
                      </Grid>

                      <Grid item xs={12}>
                        <UserField
                          id={"privacyPolicy" as UserInputTypeKey}
                          onClick={(e: any) => onLinkClick(
                            e,
                            null,
                            "https://www.finpro.de/privacy-policy"
                          )}
                          {...props}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <UserField
                          id={"termsFinproAG" as UserInputTypeKey}
                          onClick={(e: any) => onLinkClick(
                            e,
                            DocumentType.TermsFinproAG,
                            null
                          )}
                          {...props}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <UserField
                          id={"termsEffectaGmbh" as UserInputTypeKey}
                          onClick={(e: any) => onLinkClick(
                            e,
                            DocumentType.TermsEffecta,
                            null
                          )}
                          {...props}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <UserField
                          id={"customerInfoEffectaGmbh" as UserInputTypeKey}
                          onClick={(e: any) => onLinkClick(
                            e,
                            DocumentType.CustomerInfoEffecta,
                            null
                          )}
                          {...props}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <SubmitButton
                          value={t("Sign up")}
                          {...props}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography className="text--big">
                          {t("Already a member?")}{" "}
                          <span
                            className="text--link text--big"
                            onClick={() =>
                              history.push("/authentication/login")
                            }
                          >
                            {t("Login now")}
                          </span>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Container>
                </Form>
              </>
            );
          }}
        </Formik>

        <CommonErrorDialog error={error} setError={setError} />
      </Box>
    </ContentWrapper>
  );
}

export default withWidth()(Register);
